import { css } from '@emotion/react';
import { Radio } from 'antd';
import type { ReactElement } from 'react';
import React, { useId } from 'react';
import { type Env0Theme } from 'constants/themes.constants';
import styled from 'types/theme.types';
import isFunction from 'lodash/fp/isFunction';

export interface RadioGroupProps {
  items: RadioItem[];
  horizontal?: boolean;
  onChange?: React.Dispatch<React.SetStateAction<any>>;
  value?: any;
  disabled?: boolean;
}

type Element = string | ReactElement;
type isSelectedFunction = (isSelected: boolean) => Element;

interface RadioItem {
  content: isSelectedFunction | Element;
  value: any;
  disabled?: boolean;
  /**
   * Defaults to `radio-<value>`. Set it only where a call site has existing test ids that must not
   * move — D7.5 forbids touching `data-e2e`, and ttl-radio-buttons has 12 assertions on its own.
   */
  'data-e2e'?: string;
}

/* `width: 100%` lets the stacked list fill its column, but on a horizontal group it blows the row's
   basis up and crushes the input beside it — Org Policies' "Custom TTL" collapsed to its spinner
   (item N). */
const StyledRadioGroup = styled(Radio.Group, {
  shouldForwardProp: prop => prop !== 'isHorizontal'
})<{ isHorizontal?: boolean }>`
  ${(props: { isHorizontal?: boolean; theme: Env0Theme }) =>
    props.isHorizontal
      ? undefined
      : css`
          width: 100%;
        `}
`;
/**
 * `display: flex` is what makes this the vertical variant — antd's wrapper is `inline-flex`, which
 * renders every option on one line. The 5px row spacing is not a token because Figma specs no radio.
 */
const StyledVerticalRadio = styled(Radio)`
  display: flex !important;
  padding-bottom: 5px !important;
`;

const RadioGroup: React.FunctionComponent<RadioGroupProps> = ({
  items,
  horizontal,
  value: selected,
  onChange,
  disabled
}) => {
  const RadioElement = horizontal ? Radio : StyledVerticalRadio;
  // antd >= 5.13 names every group from rc-util's useId, which is the constant 'test-id' under vitest,
  // so two groups on one screen would share a DOM radio group and uncheck each other in specs.
  const name = useId();

  return (
    <StyledRadioGroup
      name={name}
      isHorizontal={horizontal}
      disabled={disabled}
      value={selected}
      onChange={e => onChange?.(e.target.value)}>
      {items?.map(({ content, value, disabled, 'data-e2e': dataE2e }) => (
        <RadioElement data-e2e={dataE2e ?? `radio-${value}`} key={value} value={value} disabled={disabled}>
          {isFunction(content) ? content(selected === value) : content}
        </RadioElement>
      ))}
    </StyledRadioGroup>
  );
};

export default RadioGroup;
